import jsPDF from 'jspdf';
import { fmtNum, fmtMonth, fmtMonthShort, slugify, fetchDistrictMillDistances, formatFortnightRange } from './utils';
import { localizedFactoryName, localizedStateName } from '../../utils/localizedDisplayName';
import { ensureKannadaCanvasFont, rasterKannadaText } from './kannadaTextRaster';
import { normalizeGrade } from '../live-tender-info/matching';

const fmtPdfDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export const LOGO_URL = '/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png';

// --- Unicode font support for Indic scripts --------------------------
// jsPDF's built-in fonts (Helvetica/Times/Courier) are the 14 standard PDF
// base fonts, WinAnsi-encoded - they cannot render Devanagari, Gujarati,
// Tamil, Telugu or Kannada glyphs at all (would draw as blank/missing
// glyphs, not even boxes, since those code points aren't in the font).
// Static Noto Sans TTFs (Regular + Bold) for each script are shipped as
// public assets (public/fonts/) and registered into jsPDF's virtual file
// system on demand via addFileToVFS/addFont - fetched and cached only when
// a PDF is actually generated in that language, so English (the default)
// never downloads anything extra.
const PDF_FONT_BY_LANGUAGE = {
  hi: 'NotoSansDevanagari',
  mr: 'NotoSansDevanagari',
  gu: 'NotoSansGujarati',
  ta: 'NotoSansTamil',
  te: 'NotoSansTelugu',
  kn: 'NotoSansKannada',
};

const fontBase64Cache = {};

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

// Registers the Unicode font for `language` into `doc` and returns its
// jsPDF font name, or null for English/unsupported languages (in which
// case callers should keep using jsPDF's default Helvetica via `undefined`).
export async function loadPdfFont(doc, language) {
  const fontFamily = PDF_FONT_BY_LANGUAGE[language];
  if (!fontFamily) return null;

  if (!fontBase64Cache[fontFamily]) {
    try {
      const [normalBuf, boldBuf] = await Promise.all([
        fetch(`/fonts/${fontFamily}-Regular.ttf`).then((r) => r.arrayBuffer()),
        fetch(`/fonts/${fontFamily}-Bold.ttf`).then((r) => r.arrayBuffer()),
      ]);
      fontBase64Cache[fontFamily] = {
        normal: arrayBufferToBase64(normalBuf),
        bold: arrayBufferToBase64(boldBuf),
      };
    } catch (e) {
      return null; // font fetch failed - caller falls back to Helvetica rather than crash the PDF
    }
  }

  const { normal, bold } = fontBase64Cache[fontFamily];
  doc.addFileToVFS(`${fontFamily}-Regular.ttf`, normal);
  doc.addFont(`${fontFamily}-Regular.ttf`, fontFamily, 'normal');
  doc.addFileToVFS(`${fontFamily}-Bold.ttf`, bold);
  doc.addFont(`${fontFamily}-Bold.ttf`, fontFamily, 'bold');
  doc.setFont(fontFamily, 'normal');
  return fontFamily;
}

// Drop-in replacement for doc.text() that transparently rasterizes Kannada
// text via canvas instead of jsPDF's vector text (see kannadaTextRaster.js
// for why - jsPDF has no Indic text-shaping engine and draws some Kannada
// glyphs overlapping their neighbor). For every other language this is a
// passthrough to doc.text() with identical behavior - doc.__kannadaRaster is
// only ever set by generateFactoryPDF, and only when language === 'kn'.
// Returns the rendered width in pt (matching doc.getTextWidth()) so callers
// that need it (e.g. underlines) don't need a separate measurement call.
function pdfText(doc, text, x, y, opts = {}) {
  if (!doc.__kannadaRaster) {
    doc.text(text, x, y, opts);
    return doc.getTextWidth(text);
  }

  const fontSize = doc.getFontSize();
  const bold = doc.__kannadaBold === true;
  const color = doc.__kannadaColor || [0, 0, 0];
  const align = opts.align || 'left';
  const lines = opts.maxWidth ? doc.splitTextToSize(text, opts.maxWidth) : [String(text)];
  const lineHeight = fontSize * 1.15;
  let maxLineWidth = 0;

  lines.forEach((line, i) => {
    const raster = rasterKannadaText(line, { fontSize, bold, color });
    const lineY = y + i * lineHeight;
    if (!raster) {
      doc.text(line, x, lineY, opts);
      return;
    }
    let drawX = x;
    if (align === 'center') drawX = x - raster.widthPt / 2;
    else if (align === 'right') drawX = x - raster.widthPt;
    doc.addImage(raster.dataUrl, 'PNG', drawX, lineY - raster.ascentPt, raster.widthPt, raster.heightPt);
    maxLineWidth = Math.max(maxLineWidth, raster.widthPt);
  });

  return maxLineWidth;
}

export async function loadImageAsDataURL(url) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return null; // logo missing shouldn't block the PDF
  }
}

// jsPDF 2.5.2's doc.link() (and textWithLink(), which calls link()
// internally) writes a link annotation's /Rect with its two y-bounds in
// the wrong order: the PDF spec requires Rect [llx lly urx ury] with
// lly <= ury, but jsPDF's internal finalBounds.y/finalBounds.h come out
// swapped, producing lly > ury. Chrome's built-in PDF viewer (PDFium) is
// strict about this and silently refuses to register clicks on the
// resulting link even though the text renders fine - confirmed by
// downloading a generated PDF, inspecting its raw /Rect entries, and
// reproducing the dead-click in Chrome. Every clickable link/button in
// every PDF this file generates goes through doc.link(), so patching it
// once per document (right after each `new jsPDF(...)`) fixes every link
// at once instead of touching each call site.
function patchJsPdfLinkRectBug(doc) {
  const originalLink = doc.link.bind(doc);
  doc.link = (...args) => {
    originalLink(...args);
    const annos = doc.internal.getCurrentPageInfo().pageContext.annotations;
    const last = annos[annos.length - 1];
    if (last && last.type === 'link' && last.finalBounds) {
      const { y, h } = last.finalBounds;
      last.finalBounds.y = h;
      last.finalBounds.h = y;
    }
  };
}

async function shareOrDownloadBlob(blob, fileName, mimeType, title, text) {
  const file = new File([blob], fileName, { type: mimeType });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text });
      return;
    } catch (e) {
      // user cancelled - fall through to download
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

// Short, meaningful filename fragment for every Market Intelligence PDF
// export, e.g. "SugarHathi_Kallappanna_Awade_Quota.pdf" - just the entity's
// first couple of meaningful words (not the full legal name, not an internal
// code), so downloaded/shared filenames stay short and readable.
export function shortName(name, wordCount = 2) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, wordCount)
    .join('_')
    .replace(/[^a-zA-Z0-9_]/g, '');
}

export function drawWatermark(doc, w, h) {
  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: 0.06 }));
  doc.setFontSize(40);
  doc.setTextColor(15, 34, 56);
  doc.setFont(undefined, 'bold');
  doc.text('SugarHathi', w / 2, h / 2, { align: 'center', angle: 30 });
  doc.restoreGraphicsState();
}

export function drawHeader(doc, w, subtitle, logoDataUrl, title = 'Factory Report', fontName) {
  doc.setDrawColor(245, 158, 11);
  doc.setLineWidth(2);
  doc.line(24, 48, w - 24, 48);
  doc.setFontSize(13);
  doc.setTextColor(17, 17, 17);
  doc.setFont(fontName || undefined, 'bold');
  pdfText(doc, title, 24, 28);
  doc.setFontSize(8);
  doc.setTextColor(136, 136, 136);
  doc.setFont(fontName || undefined, 'normal');
  pdfText(doc, subtitle, 24, 40);
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', w - 54, 14, 30, 30);
  } else {
    doc.setFillColor(245, 158, 11);
    doc.roundedRect(w - 54, 14, 30, 30, 4, 4, 'F');
    doc.setFontSize(11);
    doc.setTextColor(15, 34, 56);
    doc.setFont(undefined, 'bold');
    doc.text('SH', w - 39, 32, { align: 'center' });
  }
}

export const LEGAL_DISCLAIMER = [
  'This report is for informational purposes only, compiled from publicly available government data.',
  'SugarHathi accepts no liability for inaccuracies or decisions made based on it \u2014 for the most',
  'current figures, always refer to sugarhathi.com/market-intelligence.',
];

// Extra vertical space the boxed footer + legal text needs, reserved by
// page-break checks before this is called (was ~60 when it was a plain
// two-line disclosure; now it's a bordered block with 3 more legal lines).
export const FOOTER_RESERVED_HEIGHT = 110;

export function drawFooter(doc, w, h, disclosureLines, opts = {}) {
  const {
    fontName,
    legalText = LEGAL_DISCLAIMER.join(' '),
    taglineText = 'India\u2019s First Organised Sugar Economy \u00b7 AgriFintech',
    generatedText = (dateStr) => `Generated ${dateStr}`,
  } = opts;

  const lineH = 9;
  const padding = 8;
  const boxX = 24;
  const boxW = w - 48;
  const textW = boxW - 16;

  doc.setFontSize(7);
  doc.setFont(fontName || undefined, 'normal');
  // Each disclosure entry may be any length in any language - wrap each one
  // explicitly rather than assuming it already fits on one line (the old
  // code assumed pre-split short English lines, which breaks for languages
  // whose translations run longer or shorter).
  const wrappedGroups = [...disclosureLines, legalText].map((text) => doc.splitTextToSize(text, textW));
  const allWrappedLines = [];
  wrappedGroups.forEach((group, i) => {
    if (i > 0) allWrappedLines.push(''); // spacer between groups
    allWrappedLines.push(...group);
  });

  const boxH = padding * 2 + allWrappedLines.reduce((s, l) => s + (l === '' ? lineH * 0.4 : lineH), 0);
  const boxY = h - 34 - boxH;

  doc.setFillColor(248, 248, 248);
  doc.setDrawColor(225, 225, 225);
  doc.roundedRect(boxX, boxY, boxW, boxH, 4, 4, 'FD');

  let y = boxY + padding + 6;
  doc.setTextColor(150, 150, 150);
  allWrappedLines.forEach((line) => {
    if (line === '') { y += lineH * 0.4; return; }
    pdfText(doc, line, boxX + 8, y);
    y += lineH;
  });

  doc.setFontSize(8);
  doc.setTextColor(153, 153, 153);
  pdfText(doc, taglineText, 24, h - 12);
  const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  pdfText(doc, generatedText(today), w - 24, h - 12, { align: 'right' });
}

// Shared CTA button grid for the bottom of shareable PDFs (Factory + District).
// Each button is a real clickable region via doc.link() covering the whole
// filled rect, not just the text - works as a proper tap target, not merely
// a text hyperlink. Text-only (no icons) for now - icons would need embedded
// image assets, not yet built.
export const CTA_BLOCK_HEIGHT = 132; // reserve this much room before deciding to page-break

export function drawCTAButtons(doc, marginX, contentW, startY, opts = {}) {
  const {
    fontName,
    headerText = 'FOR MORE SUGARHATHI SERVICES, CLICK HERE:',
    labels = {},
  } = opts;
  const {
    browseSugarOffers = 'Browse Sugar Offers',
    browseVerifiedTrucks = 'Browse Verified Trucks',
    sellYourSugar = 'Sell Your Sugar',
    govtQuotaTracker = 'Govt Sugar Quota Tracker',
    registerFree = 'Register for Free',
    registerWhatsapp = 'Register via WhatsApp',
  } = labels;

  let y = startY;
  doc.setFontSize(9);
  doc.setTextColor(87, 83, 78);
  doc.setFont(fontName || undefined, 'bold');
  pdfText(doc, headerText, marginX, y);
  y += 12;

  const gap = 8;
  const btnH = 26;
  const halfW = (contentW - gap) / 2;
  const ORANGE = [249, 115, 22];
  const BLUE = [59, 130, 246];
  const GREEN = [18, 140, 126];

  const drawBtn = (x, yy, ww, label, color, url) => {
    doc.setFillColor(color[0], color[1], color[2]);
    doc.roundedRect(x, yy, ww, btnH, btnH / 2, btnH / 2, 'F');
    doc.setFont(fontName || undefined, 'bold');
    // Translated labels can run longer than the English original - shrink
    // the font just enough to keep the text inside the pill rather than
    // overflowing it.
    let fontSize = 9.5;
    doc.setFontSize(fontSize);
    const maxTextW = ww - 12;
    while (fontSize > 6.5 && doc.getTextWidth(label) > maxTextW) {
      fontSize -= 0.5;
      doc.setFontSize(fontSize);
    }
    doc.setTextColor(255, 255, 255);
    pdfText(doc, label, x + ww / 2, yy + btnH / 2 + 3.2, { align: 'center' });
    doc.link(x, yy, ww, btnH, { url });
  };

  drawBtn(marginX, y, halfW, browseSugarOffers, ORANGE, 'https://www.sugarhathi.com/sugar-marketplace-dashboard');
  drawBtn(marginX + halfW + gap, y, halfW, browseVerifiedTrucks, ORANGE, 'https://www.sugarhathi.com/truck-marketplace');
  y += btnH + gap;

  drawBtn(marginX, y, halfW, sellYourSugar, ORANGE, 'https://www.sugarhathi.com/sell-your-sugar');
  drawBtn(marginX + halfW + gap, y, halfW, govtQuotaTracker, BLUE, 'https://www.sugarhathi.com/market-intelligence');
  y += btnH + gap;

  drawBtn(marginX, y, halfW, registerFree, GREEN, 'https://www.sugarhathi.com/register');
  drawBtn(marginX + halfW + gap, y, halfW, registerWhatsapp, GREEN, 'https://wa.me/917989953468?text=Hi%2C%20I%27d%20like%20to%20register%20on%20SugarHathi');
  y += btnH + 14;

  return y;
}

// Shared PDF table for "other mills in this district, by distance" — full list
// (not top-10-capped, unlike the live-page version), farthest-first, with a
// proportional bar per row. Handles its own page breaks. Renders nothing and
// returns startY unchanged if there are no same-district mills to show.
export function drawNearbyMillsDistance(doc, w, h, marginX, contentW, startY, distances, millName, opts = {}) {
  const {
    fontName,
    language,
    headingWithName = (name) => `DISTANCE FROM ${name.toUpperCase()} TO OTHER FACTORIES IN THIS DISTRICT`,
    headingNoName = 'OTHER MILLS IN THIS DISTRICT',
    nearestFirstText = 'Nearest first',
  } = opts;

  let y = startY;
  if (!distances || distances.length === 0) return y;

  if (y > h - 80) {
    doc.addPage();
    drawWatermark(doc, w, h);
    y = 40;
  }

  doc.setFontSize(9);
  doc.setTextColor(146, 64, 14);
  doc.setFont(fontName || undefined, 'bold');
  const titleText = millName ? headingWithName(millName) : headingNoName;
  const titleLines = doc.splitTextToSize(titleText, contentW - 70);
  titleLines.forEach((line, i) => pdfText(doc, line, marginX, y + i * 11));
  doc.setFontSize(7);
  doc.setTextColor(168, 162, 158);
  doc.setFont(fontName || undefined, 'normal');
  pdfText(doc, nearestFirstText, marginX + contentW, y, { align: 'right' });
  y += titleLines.length * 11 + 6;

  const maxKm = Number(distances[distances.length - 1]?.distance_km) || 1;
  const nameColW = contentW - 90;
  const lineH = 11;

  distances.forEach((row) => {
    const displayName = language ? localizedFactoryName(row, language) : row.canonical_name;
    doc.setFontSize(9);
    doc.setFont(fontName || undefined, 'bold');
    const nameLines = doc.splitTextToSize(displayName, nameColW);
    const rowH = nameLines.length * lineH + 14;

    if (y + rowH > h - 60) {
      doc.addPage();
      drawWatermark(doc, w, h);
      y = 40;
    }

    doc.setTextColor(37, 99, 235);
    nameLines.forEach((line, i) => {
      const lineY = y + i * lineH;
      const lineW = pdfText(doc, line, marginX, lineY);
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(0.5);
      doc.line(marginX, lineY + 1.5, marginX + lineW, lineY + 1.5);
    });

    doc.setFontSize(9.5);
    doc.setTextColor(31, 41, 55);
    doc.setFont(fontName || undefined, 'bold');
    pdfText(doc, `${row.distance_km} km`, marginX + contentW, y, { align: 'right' });

    const barY = y + nameLines.length * lineH - 2;
    const barMaxW = contentW - 60;
    const pct = Math.max(0.06, Number(row.distance_km) / maxKm);
    doc.setFillColor(243, 244, 246);
    doc.roundedRect(marginX, barY, barMaxW, 3, 1.5, 1.5, 'F');
    doc.setFillColor(245, 158, 11);
    doc.roundedRect(marginX, barY, barMaxW * pct, 3, 1.5, 1.5, 'F');

    doc.link(marginX, y - lineH, contentW, rowH, {
      url: `https://www.sugarhathi.com/market-intelligence/factory/${slugify(row.canonical_name)}`,
    });

    y += rowH;
  });

  return y + 6;
}

export async function generateFactoryPDF({ mill, metrics, ranks, fyCumulative, fyLabel, fortnight = null, fortnightMt = null, history, news, chartRef, action, language = 'en', t, liveTenders = [], mandiOffer = null }) {
  // Falls back to a passthrough that fills in the inline default text (or the
  // raw key if no default given) and still substitutes {{vars}}, so this
  // function stays correct even if a caller forgets to pass `t`.
  const translate = t || ((key, defaultValue, vars) => {
    const options = typeof defaultValue === 'string' ? vars : defaultValue;
    let text = typeof defaultValue === 'string' ? defaultValue : key;
    if (options) {
      Object.entries(options).forEach(([k, v]) => { text = text.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v); });
    }
    return text;
  });
  const millName = mill ? localizedFactoryName(mill, language) : '';
  const stateName = mill ? localizedStateName(mill.source_state, language) : '';

  const logoDataUrl = await loadImageAsDataURL(LOGO_URL);
  const w = 595, h = 842; // A4 in points - full page report, matches District Snapshot PDF
  const doc = new jsPDF({ unit: 'pt', format: [w, h] });
  patchJsPdfLinkRectBug(doc);
  const marginX = 36;
  const contentW = w - marginX * 2;

  // Unicode font for the selected language - null/undefined means jsPDF's
  // default Helvetica (fine for English; Helvetica cannot render any of the
  // other 6 scripts at all, so this is not optional for them).
  const pdfFont = await loadPdfFont(doc, language);
  // jsPDF's default Helvetica (used whenever no Unicode font is loaded, i.e.
  // English) has no glyph for U+20B9 and renders it as an unrelated character -
  // the Noto Sans fonts loaded for the other 6 languages do include it, so only
  // the no-custom-font path needs the ASCII-safe fallback.
  const pdfCurrencySymbol = pdfFont ? '₹' : 'Rs. ';

  // Kannada needs its PDF text rasterized via canvas instead of jsPDF's
  // vector text path - see kannadaTextRaster.js. Wrapping setFont/
  // setTextColor on this one doc instance is how pdfText() (used throughout
  // this function and its drawHeader/drawFooter/drawCTAButtons/
  // drawNearbyMillsDistance helpers) learns the current bold/color state
  // without threading extra parameters through every call site.
  if (language === 'kn' && (await ensureKannadaCanvasFont())) {
    doc.__kannadaRaster = true;
    doc.__kannadaBold = false;
    doc.__kannadaColor = [0, 0, 0];
    const originalSetFont = doc.setFont.bind(doc);
    doc.setFont = (fontName, fontStyle) => {
      doc.__kannadaBold = fontStyle === 'bold';
      return originalSetFont(fontName, fontStyle);
    };
    const originalSetTextColor = doc.setTextColor.bind(doc);
    doc.setTextColor = (...args) => {
      if (args.length >= 3) doc.__kannadaColor = [args[0], args[1], args[2]];
      return originalSetTextColor(...args);
    };
  }

  const ctaLabels = {
    browseSugarOffers: translate('hero.browseSugarOffers', 'Browse Sugar Offers'),
    browseVerifiedTrucks: translate('hero.browseVerifiedTrucks', 'Browse Verified Trucks'),
    sellYourSugar: translate('hero.sellYourSugar', 'Sell Your Sugar'),
    govtQuotaTracker: translate('mandiFactory.govtQuotaTracker', 'Govt Sugar Quota Tracker'),
    registerFree: translate('mandiFactory.registerFree', 'Register for Free'),
    registerWhatsapp: translate('mandiFactory.registerWhatsapp', 'Register via WhatsApp'),
  };

  let chartImg = null;
  let chartAspect = 3.2;
  if (chartRef && chartRef.current) {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartRef.current, { backgroundColor: '#FFFFFF', scale: 2, useCORS: true });
      chartImg = canvas.toDataURL('image/png');
      chartAspect = canvas.width / canvas.height;
    } catch (e) {
      chartImg = null;
    }
  }

  drawWatermark(doc, w, h);
  drawHeader(
    doc, w,
    `${ctaLabels.govtQuotaTracker} \u00b7 ${translate('mandiFactory.pdfFactoryReportLabel', 'Factory Report')}`,
    logoDataUrl,
    translate('mandiFactory.pdfFactoryReportLabel', 'Factory Report'),
    pdfFont,
  );

  // Headline is the single most RECENT figure — same rule Mandi follows (see
  // mandi/data.js): a newer fortnight (typically notified ~28th of the prior
  // month, before that month's mi_release_orders row exists) REPLACES the last
  // complete month as the big number here, matching the on-page KPI hierarchy
  // (market-intelligence/factory/index.jsx shows the fortnight tile first/
  // prominent, "Last complete month" as a smaller secondary tile) — the PDF
  // previously always led with metrics.latest_qty_mt regardless, only tucking
  // the fortnight figure into the stats grid below, which is why a downloaded
  // PDF could show a stale month allocation even when the live page didn't.
  const hasFortnight = !!(fortnight && fortnightMt !== null);
  const fortnightLabelText = hasFortnight
    ? (formatFortnightRange(fortnight.periodStart, fortnight.periodEnd, language) || fortnight.periodLabel)
    : null;
  const headlineQty = hasFortnight ? fortnightMt : metrics.latest_qty_mt;
  const headlineCaption = hasFortnight
    ? translate('mandiFactory.pdfAllocationFortnight', '{{range}} allocation', { range: fortnightLabelText })
    : translate('mandiFactory.pdfAllocationMonth', '{{month}} allocation', { month: fmtMonth(metrics.latest_month) });

  doc.setFontSize(8);
  doc.setTextColor(102, 102, 102);
  doc.setFont(pdfFont || undefined, 'normal');
  pdfText(doc, (hasFortnight ? fortnightLabelText : fmtMonth(metrics.latest_month)).toUpperCase(), marginX, 62);

  // Title can wrap to 1 or 2+ lines depending on mill name length - measure
  // it first so nothing below overlaps (was a fixed y=112, which collided
  // with 2-line names like Kallappanna Awade Jawahar...).
  doc.setFontSize(15);
  doc.setTextColor(17, 17, 17);
  doc.setFont(pdfFont || undefined, 'bold');
  const titleLines = doc.splitTextToSize(millName, contentW);
  let titleY = 82;
  titleLines.forEach((line) => {
    pdfText(doc, line, marginX, titleY);
    titleY += 17;
  });

  doc.setFontSize(8.5);
  doc.setTextColor(120, 113, 108);
  doc.setFont(pdfFont || undefined, 'normal');
  pdfText(doc, `${stateName} \u00b7 ${translate('marketIntelligence.code', 'Code')} ${mill.mill_code}`, marginX, titleY - 4);
  titleY += 10;

  const bigNumY = titleY + 14;
  doc.setFontSize(24);
  doc.setTextColor(180, 83, 9);
  doc.setFont(pdfFont || undefined, 'bold');
  pdfText(doc, `${fmtNum(headlineQty)} MT`, marginX, bigNumY);
  doc.setFontSize(9);
  doc.setTextColor(102, 102, 102);
  doc.setFont(pdfFont || undefined, 'normal');
  pdfText(doc, headlineCaption, marginX, bigNumY + 14);

  const last12 = (history || []).slice(-12).reverse();
  const avg12 = last12.length ? Math.round(last12.reduce((s, r) => s + Number(r.allocated_qty_mt), 0) / last12.length) : 0;
  const notAvailable = translate('marketIntelligence.notAvailable', 'N/A');

  const stats = [
    // Once a fortnight is the headline above, "last complete month" is demoted to a
    // regular stat tile instead — mirrors the on-page KPI hierarchy exactly.
    ...(hasFortnight ? [[
      translate('marketIntelligence.completeMonthNote', 'Last complete month') + ` (${fmtMonth(metrics.latest_month)})`,
      `${fmtNum(metrics.latest_qty_mt)} MT`,
    ]] : []),
    [fyLabel, `${fmtNum(fyCumulative)} MT`],
    [`${translate('marketIntelligence.allTimePeak', 'All-time peak')} (${fmtMonth(metrics.max_qty_month)})`, `${fmtNum(metrics.max_qty_mt)} MT`],
    [translate('mandiFactory.pdfTwelveMonthAverage', '12-month average'), `${fmtNum(avg12)} MT`],
    [translate('mandiFactory.pdfGrowthCagr', 'Growth (CAGR)'), metrics.cagr_pct !== null ? `${metrics.cagr_pct >= 0 ? '+' : ''}${metrics.cagr_pct}%` : notAvailable],
    [translate('marketIntelligence.nationalRank', 'National rank'), `${ranks.national_rank} of ${ranks.totalMills}`],
    [`${translate('marketIntelligence.stateRank', 'State rank')} (${stateName})`, `${ranks.state_rank ?? notAvailable}${ranks.stateTotalMills ? ` of ${ranks.stateTotalMills}` : ''}`],
    [translate('marketIntelligence.districtRank', 'District rank'), `${ranks.district_rank ?? notAvailable}${ranks.districtTotalMills ? ` of ${ranks.districtTotalMills}` : ''}`],
    [translate('mandiFactory.pdfMonthsReporting', 'Months reporting'), `${metrics.months_reporting}`],
  ];
  const gridCols = 4;
  const gridGap = 10;
  const boxW = (contentW - (gridCols - 1) * gridGap) / gridCols;
  const boxH = 44;
  const gridY = bigNumY + 34; // dynamic - follows however many lines the title took
  stats.forEach(([label, value], i) => {
    const col = i % gridCols;
    const row = Math.floor(i / gridCols);
    const x = marginX + col * (boxW + gridGap);
    const y = gridY + row * (boxH + gridGap);
    doc.setFillColor(253, 246, 233);
    doc.roundedRect(x, y, boxW, boxH, 6, 6, 'F');
    doc.setFontSize(7);
    doc.setTextColor(120, 113, 108);
    doc.setFont(pdfFont || undefined, 'normal');
    pdfText(doc, label, x + 8, y + 17, { maxWidth: boxW - 16 });
    doc.setFontSize(10.5);
    doc.setTextColor(15, 34, 56);
    doc.setFont(pdfFont || undefined, 'bold');
    pdfText(doc, String(value), x + 8, y + 33, { maxWidth: boxW - 16 });
  });

  let cursorY = gridY + Math.ceil(stats.length / gridCols) * (boxH + gridGap) + 6;

  // Live Tender Info + Buy Now — this factory only, rendered only when it has
  // tender rows (mirrors the on-page section). Buy Now only appears per grade
  // when mandiOffer (SugarHathi's own live mill_price_quotes for this exact
  // mill_id) actually carries that grade in stock — never from tender data alone.
  if (liveTenders && liveTenders.length > 0) {
    if (cursorY > h - 120) {
      doc.addPage();
      drawWatermark(doc, w, h);
      cursorY = 40;
    }
    doc.setFontSize(11);
    doc.setTextColor(15, 34, 56);
    doc.setFont(pdfFont || undefined, 'bold');
    pdfText(doc, translate('liveTenderInfo.title', 'Live Tender Info'), marginX, cursorY);
    cursorY += 14;

    liveTenders.forEach((tender) => {
      const rowH = tender.matched ? 72 : 54;
      if (cursorY + rowH > h - 80) {
        doc.addPage();
        drawWatermark(doc, w, h);
        cursorY = 40;
      }
      doc.setFillColor(239, 246, 255);
      doc.setDrawColor(191, 219, 254);
      doc.roundedRect(marginX, cursorY, contentW, rowH, 6, 6, 'FD');

      doc.setFontSize(10.5);
      doc.setTextColor(15, 34, 56);
      doc.setFont(pdfFont || undefined, 'bold');
      pdfText(doc, `${translate('marketplace.grade', 'Grade')} ${tender.grade}`, marginX + 10, cursorY + 16);

      doc.setFontSize(11.5);
      doc.setTextColor(180, 83, 9);
      pdfText(doc, tender.rateExclGst != null ? `${pdfCurrencySymbol}${Number(tender.rateExclGst).toLocaleString('en-IN')}/MT` : '—', marginX + contentW - 10, cursorY + 16, { align: 'right' });

      const cols = [
        [translate('liveTenderInfo.tenderDateTime', 'Updated Date'), fmtPdfDate(tender.tenderDateTime)],
        [translate('marketplace.paymentDate', 'Payment Date'), fmtPdfDate(tender.paymentDate)],
        [translate('marketplace.liftingDate', 'Lifting Date'), fmtPdfDate(tender.liftingDate)],
      ];
      const tenderColW = (contentW - 20) / cols.length;
      cols.forEach(([label, value], i) => {
        const x = marginX + 10 + i * tenderColW;
        doc.setFontSize(7);
        doc.setTextColor(120, 113, 108);
        doc.setFont(pdfFont || undefined, 'normal');
        pdfText(doc, label, x, cursorY + 34);
        doc.setFontSize(9);
        doc.setTextColor(15, 34, 56);
        doc.setFont(pdfFont || undefined, 'bold');
        pdfText(doc, value, x, cursorY + 47, { maxWidth: tenderColW - 6 });
      });

      if (tender.matched && mandiOffer) {
        const quote = mandiOffer.grades.get(normalizeGrade(tender.grade));
        doc.setFillColor(255, 140, 0);
        doc.roundedRect(marginX + 10, cursorY + 54, 150, 16, 4, 4, 'F');
        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255);
        doc.setFont(pdfFont || undefined, 'bold');
        pdfText(doc, translate('liveTenderInfo.buyNow', 'Buy Now on Mandi'), marginX + 85, cursorY + 65, { align: 'center' });
        if (quote) {
          doc.link(marginX + 10, cursorY + 54, 150, 16, { url: `https://www.sugarhathi.com/mandi/${mandiOffer.factorySlug}?grade=${encodeURIComponent(quote.grade)}` });
        }
      }

      cursorY += rowH + 10;
    });

    const tenderDisclaimer = translate('liveTenderInfo.disclaimer', "Data and information displayed on this platform may be sourced from third-party sources and are provided for general informational purposes only. Such information is not owned, controlled or guaranteed by SugarHathi. SugarHathi does not represent third-party data as its own offering and does not monetise the underlying third-party information.");
    if (cursorY > h - 60) {
      doc.addPage();
      drawWatermark(doc, w, h);
      cursorY = 40;
    }
    doc.setFontSize(7.5);
    doc.setTextColor(120, 113, 108);
    doc.setFont(pdfFont || undefined, 'normal');
    doc.splitTextToSize(tenderDisclaimer, contentW).forEach((line) => { pdfText(doc, line, marginX, cursorY); cursorY += 10; });
    cursorY += 8;
  }

  if (chartImg) {
    doc.setFontSize(10);
    doc.setTextColor(15, 34, 56);
    doc.setFont(pdfFont || undefined, 'bold');
    pdfText(
      doc,
      translate('mandiFactory.pdfFullHistory', 'Full history, {{from}} \u2013 {{to}}', { from: fmtMonthShort(metrics.first_month), to: fmtMonthShort(metrics.last_month) }),
      marginX, cursorY + 12,
    );
    const imgW = contentW;
    const imgH = Math.min(imgW / chartAspect, 140);
    doc.addImage(chartImg, 'PNG', marginX, cursorY + 20, imgW, imgH);
    cursorY += 20 + imgH + 20;
  } else {
    cursorY += 10;
  }

  doc.setFontSize(11);
  doc.setTextColor(15, 34, 56);
  doc.setFont(pdfFont || undefined, 'bold');
  pdfText(doc, translate('mandiFactory.past12MonthsQuota', 'Past 12 Months Quota'), marginX, cursorY);
  cursorY += 14;

  const colMonthX = marginX;
  const colMtX = w - marginX - 6;
  const rowH = 15.5;

  doc.setFillColor(245, 237, 221);
  doc.rect(marginX, cursorY - 11, contentW, 16, 'F');
  doc.setFontSize(7.5);
  doc.setTextColor(120, 113, 108);
  doc.setFont(pdfFont || undefined, 'bold');
  pdfText(doc, translate('mandiFactory.monthLabel', 'Month'), colMonthX + 2, cursorY);
  pdfText(doc, `${translate('mandiFactory.allocationLabel', 'Allocation')} (MT)`, colMtX, cursorY, { align: 'right' });
  cursorY += 15;

  last12.forEach((r, i) => {
    if (i % 2 === 1) {
      doc.setFillColor(253, 246, 233);
      doc.rect(marginX, cursorY - 11, contentW, rowH, 'F');
    }
    doc.setFontSize(8.5);
    doc.setTextColor(68, 64, 60);
    doc.setFont(pdfFont || undefined, 'normal');
    pdfText(doc, fmtMonth(r.period_month), colMonthX + 2, cursorY);
    doc.setFont(pdfFont || undefined, 'bold');
    doc.setTextColor(15, 34, 56);
    pdfText(doc, fmtNum(r.allocated_qty_mt), colMtX, cursorY, { align: 'right' });
    cursorY += rowH;
  });

  // Latest news \u2014 headline + clickable link only, no source name shown
  cursorY += 12;
  if (cursorY > h - 100) {
    doc.addPage();
    drawWatermark(doc, w, h);
    cursorY = 40;
  }
  doc.setFontSize(11);
  doc.setTextColor(15, 34, 56);
  doc.setFont(pdfFont || undefined, 'bold');
  pdfText(doc, translate('marketIntelligence.latestNews', 'Latest news'), marginX, cursorY);
  cursorY += 16;

  if (!news || news.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(168, 162, 158);
    doc.setFont(pdfFont || undefined, 'normal');
    pdfText(doc, translate('marketIntelligence.noLatestArticles', 'No latest articles'), marginX, cursorY);
    cursorY += 16;
  } else {
    doc.setFontSize(9);
    doc.setFont(pdfFont || undefined, 'normal');
    news.forEach((n) => {
      if (cursorY > h - 70) {
        doc.addPage();
        drawWatermark(doc, w, h);
        cursorY = 40;
      }
      const lines = doc.splitTextToSize(`\u2022 ${n.title}`, contentW - 10);
      doc.setTextColor(29, 78, 216);
      lines.forEach((line) => {
        doc.textWithLink(line, marginX, cursorY, { url: n.url });
        cursorY += 12;
      });
      cursorY += 4;
    });
    cursorY += 4;
  }

  const { distances: nearbyMills } = await fetchDistrictMillDistances(mill?.id);
  cursorY = drawNearbyMillsDistance(doc, w, h, marginX, contentW, cursorY + 10, nearbyMills, millName, {
    fontName: pdfFont,
    language,
    headingWithName: (name) => translate('mandiFactory.pdfNearbyMillsWithName', 'Distance from {{name}} to other factories in this district', { name }).toUpperCase(),
    headingNoName: translate('mandiFactory.pdfNearbyMillsNoName', 'Other mills in this district').toUpperCase(),
    nearestFirstText: translate('mandiFactory.pdfNearestFirst', 'Nearest first'),
  });

  if (cursorY > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT) {
    doc.addPage();
    drawWatermark(doc, w, h);
    cursorY = 40;
  }
  cursorY = drawCTAButtons(doc, marginX, contentW, cursorY + 10, {
    fontName: pdfFont,
    headerText: translate('mandiFactory.pdfMoreServicesCta', 'FOR MORE SUGARHATHI SERVICES, CLICK HERE:'),
    labels: ctaLabels,
  });

  const disclosure = [
    translate('marketIntelligence.rankDisclosure', 'Ranks reflect {{month}} allocation only, computed by SugarHathi from published government release order data \u2014 not an official government ranking.', { month: fmtMonthShort(metrics.latest_month) }),
  ];
  drawFooter(doc, w, h, disclosure, {
    fontName: pdfFont,
    legalText: translate('mandiFactory.pdfLegalDisclaimer', LEGAL_DISCLAIMER.join(' ')),
    taglineText: translate('mandiFactory.pdfTagline', 'India\u2019s First Organised Sugar Economy \u00b7 AgriFintech'),
    generatedText: (dateStr) => translate('mandiFactory.pdfGeneratedOn', 'Generated {{date}}', { date: dateStr }),
  });

  const fileName = `SugarHathi_${shortName(mill.canonical_name)}_Quota.pdf`;
  if (action === 'download') {
    doc.save(fileName);
  } else {
    const blob = doc.output('blob');
    await shareOrDownloadBlob(blob, fileName, 'application/pdf', millName, `${millName}: ${fmtNum(headlineQty)} MT ${hasFortnight ? `(${fortnightLabelText})` : 'this month'}.`);
  }
}

export async function generateDistrictPDF({ snap, chartRef, action }) {
  const logoDataUrl = await loadImageAsDataURL(LOGO_URL);
  const w = 595, h = 842; // A4 in points - full page report, not a card
  const doc = new jsPDF({ unit: 'pt', format: [w, h] });
  patchJsPdfLinkRectBug(doc);
  const marginX = 36;
  const contentW = w - marginX * 2;

  // Capture the on-screen trend chart as an image, if a ref was provided
  let chartImg = null;
  let chartAspect = 3.2;
  if (chartRef && chartRef.current) {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartRef.current, { backgroundColor: '#FFFFFF', scale: 2, useCORS: true });
      chartImg = canvas.toDataURL('image/png');
      chartAspect = canvas.width / canvas.height;
    } catch (e) {
      chartImg = null; // chart missing shouldn't block the PDF
    }
  }

  const fortnightRangeLabel = snap.fortnightBreakdown
    ? (formatFortnightRange(snap.fortnightBreakdown.periodStart, snap.fortnightBreakdown.periodEnd) || snap.fortnightBreakdown.periodLabel)
    : null;
  const headlineTotal = snap.fortnightBreakdown ? snap.fortnightTotal : snap.latestTotal;
  const headlineLabel = fortnightRangeLabel ? `${fortnightRangeLabel} allocation` : `${fmtMonth(snap.latestMonth)} allocation`;

  drawWatermark(doc, w, h);
  drawHeader(doc, w, 'Govt Sugar Quota Tracker \u00b7 District Report', logoDataUrl);

  doc.setFontSize(8);
  doc.setTextColor(102, 102, 102);
  doc.setFont(undefined, 'normal');
  doc.text((fortnightRangeLabel || fmtMonth(snap.latestMonth)).toUpperCase(), marginX, 62);
  doc.setFontSize(17);
  doc.setTextColor(17, 17, 17);
  doc.setFont(undefined, 'bold');
  doc.text(`${snap.districtName}, ${snap.stateName}`, marginX, 84, { maxWidth: contentW });

  doc.setFontSize(24);
  doc.setTextColor(180, 83, 9);
  doc.setFont(undefined, 'bold');
  doc.text(`${fmtNum(headlineTotal)} MT`, marginX, 112);
  doc.setFontSize(9);
  doc.setTextColor(102, 102, 102);
  doc.setFont(undefined, 'normal');
  doc.text(headlineLabel, marginX, 126);

  // Stat grid - 4 columns
  const stats = [
    ['Mills active', `${snap.millsActive} of ${snap.millsReporting}`],
    [snap.fyLabel, `${fmtNum(snap.fyTotal)} MT`],
    ...(fortnightRangeLabel ? [[`${fortnightRangeLabel} (MT)`, `${fmtNum(snap.fortnightTotal)} MT`]] : []),
    ['State rank', `${snap.stateRank} of ${snap.stateTotal}`],
    ['National rank', `${snap.natRank} of ${snap.natTotal}`],
    ['Share of state', snap.stateShare !== null ? `${snap.stateShare.toFixed(1)}%` : 'N/A'],
    ['Share of India', snap.natShare !== null ? `${snap.natShare.toFixed(2)}%` : 'N/A'],
  ];
  const gridCols = 4;
  const gridGap = 10;
  const boxW = (contentW - (gridCols - 1) * gridGap) / gridCols;
  const boxH = 44;
  const gridY = 144;
  stats.forEach(([label, value], i) => {
    const col = i % gridCols;
    const row = Math.floor(i / gridCols);
    const x = marginX + col * (boxW + gridGap);
    const y = gridY + row * (boxH + gridGap);
    doc.setFillColor(253, 246, 233);
    doc.roundedRect(x, y, boxW, boxH, 6, 6, 'F');
    doc.setFontSize(7);
    doc.setTextColor(120, 113, 108);
    doc.setFont(undefined, 'normal');
    doc.text(label, x + 8, y + 17, { maxWidth: boxW - 16 });
    doc.setFontSize(10.5);
    doc.setTextColor(15, 34, 56);
    doc.setFont(undefined, 'bold');
    doc.text(String(value), x + 8, y + 33, { maxWidth: boxW - 16 });
  });

  let cursorY = gridY + Math.ceil(stats.length / gridCols) * (boxH + gridGap) + 6;

  // Trend chart image
  if (chartImg) {
    doc.setFontSize(10);
    doc.setTextColor(15, 34, 56);
    doc.setFont(undefined, 'bold');
    doc.text(`Last ${snap.trend.length} months`, marginX, cursorY + 12);
    const imgW = contentW;
    const imgH = Math.min(imgW / chartAspect, 150);
    doc.addImage(chartImg, 'PNG', marginX, cursorY + 20, imgW, imgH);
    cursorY += 20 + imgH + 20;
  } else {
    cursorY += 10;
  }

  // Factories table (paginated)
  doc.setFontSize(11);
  doc.setTextColor(15, 34, 56);
  doc.setFont(undefined, 'bold');
  doc.text(`Factories in ${snap.districtName} (${snap.mills.length})`, marginX, cursorY);
  cursorY += 14;

  const colRankX = marginX;
  const colNameX = marginX + 26;
  const colMtX = w - marginX - 6;
  const nameMaxW = colMtX - colNameX - 60;

  const drawTableHeader = (y) => {
    doc.setFillColor(245, 237, 221);
    doc.rect(marginX, y - 11, contentW, 16, 'F');
    doc.setFontSize(7.5);
    doc.setTextColor(120, 113, 108);
    doc.setFont(undefined, 'bold');
    doc.text('#', colRankX + 2, y);
    doc.text('Factory', colNameX, y);
    doc.text(fortnightRangeLabel ? `${fortnightRangeLabel} MT` : `${fmtMonthShort(snap.latestMonth)} MT`, colMtX, y, { align: 'right' });
    return y + 15;
  };

  cursorY = drawTableHeader(cursorY);
  const bottomLimit = h - 60;

  snap.mills.forEach((m, i) => {
    if (cursorY > bottomLimit) {
      doc.addPage();
      drawWatermark(doc, w, h);
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.setFont(undefined, 'normal');
      doc.text(`${snap.districtName} \u2014 factories, continued`, marginX, 30);
      cursorY = 52;
      cursorY = drawTableHeader(cursorY);
    }
    const lines = doc.splitTextToSize(m.canonical_mill_name, nameMaxW);
    const displayName = lines.length > 1 ? `${lines[0].replace(/\s+$/, '')}\u2026` : lines[0];

    if (i % 2 === 1) {
      doc.setFillColor(253, 246, 233);
      doc.rect(marginX, cursorY - 11, contentW, 15.5, 'F');
    }

    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(String(i + 1), colRankX + 2, cursorY);

    doc.setTextColor(37, 99, 235);
    doc.text(displayName, colNameX, cursorY);
    const nameW = doc.getTextWidth(displayName);
    doc.setDrawColor(37, 99, 235);
    doc.setLineWidth(0.5);
    doc.line(colNameX, cursorY + 1.5, colNameX + nameW, cursorY + 1.5);
    doc.link(colNameX, cursorY - 9, nameW, 12, {
      url: `https://www.sugarhathi.com/market-intelligence/factory/${slugify(m.canonical_mill_name)}`,
    });

    doc.setFont(undefined, 'bold');
    const mVal = snap.fortnightBreakdown ? (snap.fortnightBreakdown.byMill.get(m.mill_id) ?? 0) : m.currentMT;
    if (mVal > 0) doc.setTextColor(180, 83, 9);
    else doc.setTextColor(168, 162, 158);
    doc.text(fmtNum(mVal), colMtX, cursorY, { align: 'right' });
    cursorY += 15.5;
  });

  // Latest news \u2014 headline + clickable link only, no source name shown
  cursorY += 8;
  if (cursorY > h - 100) {
    doc.addPage();
    drawWatermark(doc, w, h);
    cursorY = 40;
  }
  doc.setFontSize(11);
  doc.setTextColor(15, 34, 56);
  doc.setFont(undefined, 'bold');
  doc.text('Latest news', marginX, cursorY);
  cursorY += 16;

  if (!snap.news || snap.news.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(168, 162, 158);
    doc.setFont(undefined, 'normal');
    doc.text('No latest articles', marginX, cursorY);
    cursorY += 16;
  } else {
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    snap.news.forEach((n) => {
      if (cursorY > h - 70) {
        doc.addPage();
        drawWatermark(doc, w, h);
        cursorY = 40;
      }
      const lines = doc.splitTextToSize(`\u2022 ${n.title}`, contentW - 10);
      doc.setTextColor(29, 78, 216);
      lines.forEach((line) => {
        doc.textWithLink(line, marginX, cursorY, { url: n.url });
        cursorY += 12;
      });
      cursorY += 4;
    });
    cursorY += 4;
  }

  if (cursorY > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT) {
    doc.addPage();
    drawWatermark(doc, w, h);
    cursorY = 40;
  }
  cursorY = drawCTAButtons(doc, marginX, contentW, cursorY + 10);

  const disclosure = [
    `Ranks reflect ${fmtMonthShort(snap.latestMonth)} allocation only, computed by SugarHathi`,
    `from published government data \u2014 not an official ranking.`,
  ];
  drawFooter(doc, w, h, disclosure);

  const fileName = `SugarHathi_${shortName(snap.districtName)}_District.pdf`;
  if (action === 'download') {
    doc.save(fileName);
  } else {
    const blob = doc.output('blob');
    await shareOrDownloadBlob(blob, fileName, 'application/pdf', snap.districtName, `${snap.districtName}: ${fmtNum(headlineTotal)} MT ${fortnightRangeLabel ? `(${fortnightRangeLabel})` : 'this month'}.`);
  }
}

export async function generateComparisonPDF({ mills, monthLabel, fyLabel, fortnight = null, action }) {
  const [a, b] = mills;
  const logoDataUrl = await loadImageAsDataURL(LOGO_URL);
  const w = 595, h = 842; // full A4, matching Factory/District PDFs - h=580 was too short once the 12-month table was added, causing an early forced page-break with a big blank gap before it
  const doc = new jsPDF({ unit: 'pt', format: [w, h] });
  patchJsPdfLinkRectBug(doc);
  const marginX = 36;
  const contentW = w - marginX * 2;

  drawWatermark(doc, w, h);
  drawHeader(doc, w, `Govt Sugar Quota Tracker \u00b7 ${monthLabel}`, logoDataUrl);
  doc.setFontSize(15);
  doc.setTextColor(17, 17, 17);
  doc.setFont(undefined, 'bold');
  doc.text('Factory Comparison', marginX, 74);

  const colW = (contentW - 24) / 2;
  [a, b].forEach((m, i) => {
    const x = marginX + i * (colW + 24);
    doc.setFillColor(i === 0 ? 245 : 59, i === 0 ? 158 : 130, i === 0 ? 11 : 246);
    doc.rect(x, 92, 8, 8, 'F');
    doc.setFontSize(9);
    doc.setTextColor(17, 17, 17);
    doc.setFont(undefined, 'bold');
    doc.text(i === 0 ? 'FACTORY A' : 'FACTORY B', x + 13, 99);
    doc.setFontSize(9.5);
    doc.setTextColor(51, 51, 51);
    doc.setFont(undefined, 'normal');
    doc.text(m.canonical_name, x, 114, { maxWidth: colW });
    doc.setFontSize(8);
    doc.setTextColor(120, 113, 108);
    doc.text(`Code ${m.mill_code}`, x, 128, { maxWidth: colW });
  });

  const fortnightRangeLabel = fortnight ? `${formatFortnightRange(fortnight.periodStart, fortnight.periodEnd) || fortnight.periodLabel} (MT)` : '';
  const rows = [
    ['Metric', a.metrics?.latest_qty_mt !== undefined ? fmtMonthShort(a.metrics.latest_month) : '', ''],
    ...(fortnight ? [[fortnightRangeLabel, fmtNum(a.fortnightMt), fmtNum(b.fortnightMt)]] : []),
    [monthLabel, fmtNum(a.metrics?.latest_qty_mt), fmtNum(b.metrics?.latest_qty_mt)],
    [fyLabel || 'Apr onwards', fmtNum(a.fyCumulative), fmtNum(b.fyCumulative)],
    ['All-time peak', `${fmtNum(a.metrics?.max_qty_mt)}${a.metrics?.max_qty_month ? ` (${fmtMonth(a.metrics.max_qty_month)})` : ''}`, `${fmtNum(b.metrics?.max_qty_mt)}${b.metrics?.max_qty_month ? ` (${fmtMonth(b.metrics.max_qty_month)})` : ''}`],
    ['National rank', `${a.rank?.national_rank ?? 'N/A'}`, `${b.rank?.national_rank ?? 'N/A'}`],
    ['State rank', `${a.rank?.state_rank ?? 'N/A'}${a.stateTotalMills ? `/${a.stateTotalMills}` : ''}`, `${b.rank?.state_rank ?? 'N/A'}${b.stateTotalMills ? `/${b.stateTotalMills}` : ''}`],
    ['District rank', `${a.rank?.district_rank ?? 'N/A'}${a.districtTotalMills ? `/${a.districtTotalMills}` : ''}`, `${b.rank?.district_rank ?? 'N/A'}${b.districtTotalMills ? `/${b.districtTotalMills}` : ''}`],
  ];

  let y = 148;
  rows.forEach(([label, valA, valB], i) => {
    if (i === 0) {
      doc.setFillColor(247, 247, 247);
      doc.rect(marginX, y - 10, contentW, 16, 'F');
      doc.setFontSize(8);
      doc.setTextColor(102, 102, 102);
      doc.setFont(undefined, 'bold');
      doc.text('Metric', marginX + 4, y);
      doc.setTextColor(245, 158, 11);
      doc.text('A', marginX + contentW / 2 + colW / 2 - 4, y, { align: 'right' });
      doc.setTextColor(59, 130, 246);
      doc.text('B', w - marginX - 4, y, { align: 'right' });
      y += 20;
      return;
    }
    doc.setDrawColor(238, 238, 238);
    doc.line(marginX, y + 4, w - marginX, y + 4);
    doc.setFontSize(9.5);
    doc.setTextColor(102, 102, 102);
    doc.setFont(undefined, 'normal');
    doc.text(label, marginX + 4, y);
    doc.setTextColor(17, 17, 17);
    doc.setFont(undefined, 'bold');
    doc.text(String(valA), marginX + contentW / 2 + colW / 2 - 4, y, { align: 'right' });
    doc.text(String(valB), w - marginX - 4, y, { align: 'right' });
    y += 20;
  });

  // Last 12 months, side by side - shows how each factory has actually been
  // trending, in addition to the summary rows above (not a replacement).
  y += 20;
  const allMonths = [...new Set([...(a.history || []).map((r) => r.period_month), ...(b.history || []).map((r) => r.period_month)])].sort();
  const last12Months = allMonths.slice(-12);
  const monthRowH = 16;
  const monthTableHeight = 24 + last12Months.length * monthRowH;
  if (y + monthTableHeight > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT) {
    doc.addPage();
    drawWatermark(doc, w, h);
    y = 40;
  }
  doc.setFontSize(11);
  doc.setTextColor(15, 34, 56);
  doc.setFont(undefined, 'bold');
  doc.text('Last 12 months', marginX, y);
  y += 16;

  doc.setFillColor(247, 247, 247);
  doc.rect(marginX, y - 11, contentW, 16, 'F');
  doc.setFontSize(8);
  doc.setTextColor(102, 102, 102);
  doc.setFont(undefined, 'bold');
  doc.text('Month', marginX + 4, y);
  doc.setTextColor(245, 158, 11);
  doc.text('A (MT)', marginX + contentW / 2 + colW / 2 - 4, y, { align: 'right' });
  doc.setTextColor(59, 130, 246);
  doc.text('B (MT)', w - marginX - 4, y, { align: 'right' });
  y += monthRowH;

  last12Months.forEach((pm, i) => {
    const av = (a.history || []).find((r) => r.period_month === pm);
    const bv = (b.history || []).find((r) => r.period_month === pm);
    if (i % 2 === 1) {
      doc.setFillColor(253, 246, 233);
      doc.rect(marginX, y - 11, contentW, monthRowH, 'F');
    }
    doc.setFontSize(9);
    doc.setTextColor(68, 64, 60);
    doc.setFont(undefined, 'normal');
    doc.text(fmtMonth(pm), marginX + 4, y);
    doc.setTextColor(15, 34, 56);
    doc.setFont(undefined, 'bold');
    doc.text(av ? fmtNum(av.allocated_qty_mt) : '\u2014', marginX + contentW / 2 + colW / 2 - 4, y, { align: 'right' });
    doc.text(bv ? fmtNum(bv.allocated_qty_mt) : '\u2014', w - marginX - 4, y, { align: 'right' });
    y += monthRowH;
  });

  y += 14;
  if (y > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT) {
    doc.addPage();
    drawWatermark(doc, w, h);
    y = 40;
  }
  y = drawCTAButtons(doc, marginX, contentW, y);

  const disclosure = [
    `Ranks reflect ${monthLabel} allocation only, computed by SugarHathi`,
    `from published government data \u2014 not an official ranking.`,
  ];
  drawFooter(doc, w, h, disclosure);

  const fileName = `SugarHathi_${shortName(a.canonical_name)}_vs_${shortName(b.canonical_name)}_Comparison.pdf`;
  if (action === 'download') {
    doc.save(fileName);
  } else {
    const blob = doc.output('blob');
    await shareOrDownloadBlob(blob, fileName, 'application/pdf', 'Factory Comparison', `${a.canonical_name} vs ${b.canonical_name}`);
  }
}

// Shared row-list table renderer for the Districts/States browse-page PDFs.
// Same visual rules as the Factory/District/Comparison PDFs: watermark,
// header, alternating row shading, boxed footer + legal disclaimer, CTA grid.
function drawRankedListTable(doc, w, h, marginX, contentW, nameColLabel, rows, getName, getMillsFraction, getMt, continuedLabel) {
  const colRankX = marginX;
  const colNameX = marginX + 26;
  const colMillsX = w - marginX - 100;
  const colMtX = w - marginX - 6;
  const nameMaxW = colMillsX - colNameX - 10;

  const drawTableHeader = (yy) => {
    doc.setFillColor(245, 237, 221);
    doc.rect(marginX, yy - 11, contentW, 16, 'F');
    doc.setFontSize(7.5);
    doc.setTextColor(120, 113, 108);
    doc.setFont(undefined, 'bold');
    doc.text('#', colRankX + 2, yy);
    doc.text(nameColLabel, colNameX, yy);
    doc.text('Mills', colMillsX, yy, { align: 'right' });
    doc.text('MT', colMtX, yy, { align: 'right' });
    return yy + 15;
  };

  let y = drawTableHeader(100);
  const bottomLimit = h - 60;

  rows.forEach((r, i) => {
    if (y > bottomLimit) {
      doc.addPage();
      drawWatermark(doc, w, h);
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.setFont(undefined, 'normal');
      doc.text(continuedLabel, marginX, 30);
      y = 52;
      y = drawTableHeader(y);
    }
    if (i % 2 === 1) {
      doc.setFillColor(253, 246, 233);
      doc.rect(marginX, y - 11, contentW, 15.5, 'F');
    }
    const lines = doc.splitTextToSize(getName(r), nameMaxW);
    const displayName = lines.length > 1 ? `${lines[0].replace(/\s+$/, '')}\u2026` : lines[0];

    doc.setFontSize(8);
    doc.setTextColor(68, 64, 60);
    doc.setFont(undefined, 'normal');
    doc.text(String(i + 1), colRankX + 2, y);
    doc.text(displayName, colNameX, y);
    doc.text(getMillsFraction(r), colMillsX, y, { align: 'right' });
    doc.setFont(undefined, 'bold');
    doc.setTextColor(180, 83, 9);
    doc.text(fmtNum(getMt(r)), colMtX, y, { align: 'right' });
    y += 15.5;
  });

  return y;
}

export async function generateDistrictsListPDF({ districts, month, stateName, fortnight, action }) {
  const logoDataUrl = await loadImageAsDataURL(LOGO_URL);
  const w = 595, h = 842; // full A4, same rules as Factory/District/Comparison PDFs
  const doc = new jsPDF({ unit: 'pt', format: [w, h] });
  patchJsPdfLinkRectBug(doc);
  const marginX = 36;
  const contentW = w - marginX * 2;

  const rangeLabel = fortnight ? (formatFortnightRange(fortnight.periodStart, fortnight.periodEnd, 'en') || fortnight.periodLabel) : null;

  drawWatermark(doc, w, h);
  drawHeader(doc, w, `Govt Sugar Quota Tracker \u00b7 ${rangeLabel || fmtMonth(month)}`, logoDataUrl);

  doc.setFontSize(15);
  doc.setTextColor(17, 17, 17);
  doc.setFont(undefined, 'bold');
  const titleText = stateName ? `Districts in ${stateName} (${districts.length})` : `All Districts (${districts.length})`;
  doc.text(titleText, marginX, 74);

  let y = drawRankedListTable(
    doc, w, h, marginX, contentW,
    'District / State',
    districts,
    (d) => `${d.canonical_district}, ${d.canonical_state}`,
    (d) => `${d.mills_active}/${d.mills_reporting}`,
    (d) => (fortnight ? (d.fortnightMt ?? 0) : d.total_allocated_mt),
    'Districts, continued',
  );

  y += 8;
  if (y > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT) {
    doc.addPage();
    drawWatermark(doc, w, h);
    y = 40;
  }
  y = drawCTAButtons(doc, marginX, contentW, y + 10);

  const disclosure = [
    `Figures reflect ${rangeLabel || fmtMonth(month)} allocation only, computed by SugarHathi`,
    `from published government data \u2014 not an official ranking.`,
  ];
  drawFooter(doc, w, h, disclosure);

  const fileName = stateName ? `SugarHathi_${shortName(stateName)}_Districts.pdf` : 'SugarHathi_All_Districts.pdf';
  const shareTitle = stateName ? `Districts in ${stateName}` : 'All Districts';
  if (action === 'download') {
    doc.save(fileName);
  } else {
    const blob = doc.output('blob');
    await shareOrDownloadBlob(blob, fileName, 'application/pdf', shareTitle, `${districts.length} districts, ${rangeLabel || fmtMonth(month)} allocation data.`);
  }
}

export async function generateStatesListPDF({ states, month, fortnight, action }) {
  const logoDataUrl = await loadImageAsDataURL(LOGO_URL);
  const w = 595, h = 842; // full A4, same rules as Factory/District/Comparison PDFs
  const doc = new jsPDF({ unit: 'pt', format: [w, h] });
  patchJsPdfLinkRectBug(doc);
  const marginX = 36;
  const contentW = w - marginX * 2;

  const rangeLabel = fortnight ? (formatFortnightRange(fortnight.periodStart, fortnight.periodEnd, 'en') || fortnight.periodLabel) : null;

  drawWatermark(doc, w, h);
  drawHeader(doc, w, `Govt Sugar Quota Tracker \u00b7 ${rangeLabel || fmtMonth(month)}`, logoDataUrl);

  doc.setFontSize(15);
  doc.setTextColor(17, 17, 17);
  doc.setFont(undefined, 'bold');
  doc.text(`All States (${states.length})`, marginX, 74);

  let y = drawRankedListTable(
    doc, w, h, marginX, contentW,
    'State',
    states,
    (s) => s.canonical_state,
    (s) => `${s.mills_active}/${s.mills_reporting}`,
    (s) => (fortnight ? (s.fortnightMt ?? 0) : s.total_allocated_mt),
    'States, continued',
  );

  y += 8;
  if (y > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT) {
    doc.addPage();
    drawWatermark(doc, w, h);
    y = 40;
  }
  y = drawCTAButtons(doc, marginX, contentW, y + 10);

  const disclosure = [
    `Figures reflect ${rangeLabel || fmtMonth(month)} allocation only, computed by SugarHathi`,
    `from published government data \u2014 not an official ranking.`,
  ];
  drawFooter(doc, w, h, disclosure);

  const fileName = 'SugarHathi_All_States.pdf';
  if (action === 'download') {
    doc.save(fileName);
  } else {
    const blob = doc.output('blob');
    await shareOrDownloadBlob(blob, fileName, 'application/pdf', 'All States', `${states.length} states, ${rangeLabel || fmtMonth(month)} allocation data.`);
  }
}

function drawStatewiseTable(doc, w, h, marginX, contentW, rows, sepLabel, monthShort, continuedLabel) {
  // Column x-positions are content-tight (gap sized to what the text
  // actually needs, not stretched to fill the page width - stretching
  // was what caused the big dead gap between Active and the month columns).
  // The table block is narrower than the full page width as a result, so
  // it's centered within contentW instead of hugging the left margin -
  // that way the leftover space is split evenly on both sides.
  //
  // Only Sep 1-15 and Aug (last complete month) are shown, side by side -
  // no Diff/% column, since comparing a half-month fortnight against a full
  // month as a percent change isn't a meaningful figure.
  const TBL_W = 360; // must match the span from colRankX to colAugRight below (+22 padding)
  const tblX = marginX + (contentW - TBL_W) / 2;
  const colRankX = tblX;
  const colStateX = tblX + 18;
  const colMillsX = tblX + 143;
  const colSepRight = tblX + 278;
  const colAugRight = tblX + 338;
  const stateMaxW = colMillsX - colStateX - 8;
  const ROW_H = 19;
  const HEADER_H = 27; // taller than a data row - "Active Factories" wraps to 2 lines

  const drawTableHeader = (yy) => {
    doc.setFillColor(15, 34, 56);
    doc.rect(tblX, yy - 16, TBL_W, HEADER_H, 'F');
    doc.setFontSize(9);
    doc.setTextColor(253, 248, 240);
    doc.setFont(undefined, 'bold');
    doc.text('#', colRankX + 2, yy);
    doc.text('State', colStateX, yy);
    doc.text('Active', colMillsX, yy - 5);
    doc.text('Factories', colMillsX, yy + 6);
    doc.text(sepLabel, colSepRight, yy, { align: 'right' });
    doc.text(monthShort, colAugRight, yy, { align: 'right' });
    return yy + 24; // header bottom (yy-16+HEADER_H=yy+11) plus 13 to match the row loop's (y-13) rect-top convention - without this, row 1's background painted over the bottom of the header
  };

  let y = drawTableHeader(104);
  const bottomLimit = h - 60;

  rows.forEach((r, i) => {
    if (y > bottomLimit) {
      doc.addPage();
      drawWatermark(doc, w, h);
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.setFont(undefined, 'normal');
      doc.text(continuedLabel, marginX, 30);
      y = 52;
      y = drawTableHeader(y);
    }

    if (i % 2 === 1) {
      doc.setFillColor(253, 246, 233);
      doc.rect(tblX, y - 13, TBL_W, ROW_H, 'F');
    }

    const lines = doc.splitTextToSize(r.state, stateMaxW);
    const displayName = lines.length > 1 ? `${lines[0].replace(/\s+$/, '')}\u2026` : lines[0];

    doc.setFontSize(11);
    doc.setTextColor(68, 64, 60);
    doc.setFont(undefined, 'normal');
    doc.text(String(i + 1), colRankX + 2, y);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(28, 25, 23);
    doc.text(displayName, colStateX, y);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(68, 64, 60);
    doc.text(`${r.millsActive}/${r.millsReporting}`, colMillsX, y);

    doc.setFont(undefined, 'bold');
    doc.setTextColor(180, 83, 9);
    doc.text(fmtNum(r.sep), colSepRight, y, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setTextColor(28, 25, 23);
    doc.text(fmtNum(r.aug), colAugRight, y, { align: 'right' });

    y += ROW_H;
  });

  return y;
}

function fmtNotifDatePDF(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export async function generateStatewiseAllocationPDF({ rows, month, fortnight = null, action }) {
  const logoDataUrl = await loadImageAsDataURL(LOGO_URL);
  const w = 595, h = 842;
  const doc = new jsPDF({ unit: 'pt', format: [w, h] });
  patchJsPdfLinkRectBug(doc);
  const marginX = 36;
  const contentW = w - marginX * 2;

  const sepRangeLabel = fortnight ? (formatFortnightRange(fortnight.periodStart, fortnight.periodEnd) || fortnight.periodLabel) : 'Sep 1-15';

  drawWatermark(doc, w, h);
  drawHeader(doc, w, `Govt Sugar Quota Tracker \u00b7 ${fmtMonth(month)}`, logoDataUrl, 'Statewise Allocation Report');

  doc.setFontSize(15);
  doc.setTextColor(17, 17, 17);
  doc.setFont(undefined, 'bold');
  doc.text(`Statewise Allocation (${rows.length} states)`, marginX, 74);
  doc.setFontSize(9.5);
  doc.setTextColor(120, 113, 108);
  doc.setFont(undefined, 'normal');
  doc.text(`${sepRangeLabel} vs ${fmtMonthShort(month)} (last complete month), sorted by ${sepRangeLabel}`, marginX, 86);

  let y = drawStatewiseTable(doc, w, h, marginX, contentW, rows, sepRangeLabel, fmtMonthShort(month), 'States, continued');

  // Total row
  const totals = rows.reduce((acc, r) => ({
    sep: acc.sep + r.sep, aug: acc.aug + r.aug,
    millsActive: acc.millsActive + r.millsActive, millsReporting: acc.millsReporting + r.millsReporting,
  }), { sep: 0, aug: 0, millsActive: 0, millsReporting: 0 });
  const zeroMtCount = totals.millsReporting - totals.millsActive;

  if (y > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT - 60) {
    doc.addPage();
    drawWatermark(doc, w, h);
    y = 40;
  }
  const tblW = 360; // must match TBL_W in drawStatewiseTable
  const tblX = marginX + (contentW - tblW) / 2; // must match tblX in drawStatewiseTable
  doc.setFillColor(245, 158, 11);
  doc.rect(tblX, y - 13, tblW, 18.5, 'F');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 34, 56);
  doc.setFont(undefined, 'bold');
  doc.text('Total', tblX + 18, y);
  doc.text(`${totals.millsActive}/${totals.millsReporting}`, tblX + 143, y);
  doc.text(fmtNum(totals.sep), tblX + 278, y, { align: 'right' });
  doc.text(fmtNum(totals.aug), tblX + 338, y, { align: 'right' });
  y += 26;

  // Zero MT callout
  doc.setFillColor(254, 242, 242);
  doc.setDrawColor(252, 165, 165);
  doc.roundedRect(marginX, y, contentW, 30, 4, 4, 'FD');
  doc.setFontSize(10);
  doc.setTextColor(185, 28, 28);
  doc.setFont(undefined, 'bold');
  doc.text('Factories with Zero MT Allocation', marginX + 10, y + 13);
  doc.setFontSize(13);
  doc.text(`${zeroMtCount} of ${totals.millsReporting} factories`, marginX + 10, y + 24);
  y += 42;

  // SugarHathi summary box - no % change claim (half-month vs full-month
  // comparison isn't a meaningful percentage), just the two totals side by side.
  const summaryText = `National allocation for ${sepRangeLabel} stood at ${fmtNum(totals.sep)} MT across ${rows.length} states. ${fmtMonth(month)} (last complete month) stood at ${fmtNum(totals.aug)} MT. ${totals.millsActive} of ${totals.millsReporting} factories were active in ${fmtMonth(month)}, leaving ${zeroMtCount} at zero allocation.`;

  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const summaryLines = doc.splitTextToSize(summaryText, contentW - 20);
  const summaryBoxH = 28 + summaryLines.length * 13.5;

  if (y + summaryBoxH > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT) {
    doc.addPage();
    drawWatermark(doc, w, h);
    y = 40;
  }

  doc.setFillColor(231, 247, 239);
  doc.setDrawColor(183, 232, 206);
  doc.roundedRect(marginX, y, contentW, summaryBoxH, 6, 6, 'FD');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 34, 56);
  doc.setFont(undefined, 'bold');
  doc.text('SugarHathi summary', marginX + 10, y + 16);
  doc.setFontSize(10);
  doc.setTextColor(54, 83, 20);
  doc.setFont(undefined, 'normal');
  let sy = y + 30;
  summaryLines.forEach((line) => { doc.text(line, marginX + 10, sy, { maxWidth: contentW - 20 }); sy += 13.5; });
  y += summaryBoxH + 10;

  // Govt notification number/date - below the summary box, above the footer CTA buttons
  const notifStr = fortnight?.notificationNo
    ? `Govt. Order No. ${fortnight.notificationNo}${fmtNotifDatePDF(fortnight.notificationDate) ? `, dated ${fmtNotifDatePDF(fortnight.notificationDate)}` : ''}`
    : 'Govt. order details pending';
  doc.setFontSize(9.5);
  doc.setTextColor(120, 113, 108);
  doc.setFont(undefined, 'normal');
  doc.text(notifStr, marginX, y);
  y += 16;

  if (y > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT) {
    doc.addPage();
    drawWatermark(doc, w, h);
    y = 40;
  }
  y = drawCTAButtons(doc, marginX, contentW, y + 10);

  const disclosure = [
    `Figures reflect ${sepRangeLabel} vs ${fmtMonth(month)} (last complete month), computed by SugarHathi`,
    `from published government data \u2014 not an official ranking.`,
  ];
  drawFooter(doc, w, h, disclosure);

  const monthTag = month ? new Date(month).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }).replace(' ', '_') : '';
  const fileName = `SugarHathi_Statewise_Allocation${monthTag ? `_${monthTag}` : ''}.pdf`;
  if (action === 'download') {
    doc.save(fileName);
  } else {
    const blob = doc.output('blob');
    await shareOrDownloadBlob(blob, fileName, 'application/pdf', 'Statewise Allocation', `${rows.length} states, ${sepRangeLabel} vs ${fmtMonth(month)}.`);
  }
}

export async function captureAndShareImage(elementRef, fileName, title, text) {
  const html2canvas = (await import('html2canvas')).default;
  const canvas = await html2canvas(elementRef.current, { backgroundColor: '#0F2238', scale: 2, useCORS: true });
  return new Promise((resolve) => {
    canvas.toBlob(async (blob) => {
      if (!blob) { resolve(); return; }
      await shareOrDownloadBlob(blob, fileName, 'image/png', title, text);
      resolve();
    }, 'image/png');
  });
}

